import React, {
  createContext,
  useCallback,
  useState,
  useEffect,
  useContext,
} from 'react';
import useSessionStorage from '../hooks/useSessionStorage';
import * as TestStatus from '../components/assessment/TestStatus.js';
import * as Storage from '../components/common/Storage.js';
import { PROCTOR_PAUSE_DIALOG } from '../components/assessment/LearnosityDialogs.js';
import { KioskContext } from './KioskProviderContext.js';
import { WINDOWS } from '../scripts/Platform.js';
import { closeWindowsSecureBrowser } from '../scripts/KioskModeChecker.js';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE } from '../i18n.js';
import { checkForSecureWrapper, exitSecureWrapper } from '../scripts/secure-wrapper/enforceSecureWrapper.js';

//Default to an empty object so components rendered outside of the
//AssessmentProvider (ex: SecureWrapper on the login/landing routes) can
//safely read from this context.
export const AssessmentContext = createContext({});

/**
 *
 * @param {string} props.wssURL URL for the websocket
 * @param {string} props.authToken The authentication token to be passed for websocket connection
 * @returns
 */
export const AssessmentProvider = ({ practice, loginData, children }) => {

  const { i18n } = useTranslation();  
  const { isSecure, platform, displayMode } = useContext(KioskContext);
  const { getStorageItem, setStorageItem, removeStorageItem, clearStorage } =
    useSessionStorage();
  const __signedObject = getStorageItem(Storage.STORAGE_SIGNED_OBJECT);

  //Set the save and error state from session storage
  const __saveErrorCount =
    getStorageItem(Storage.STORAGE_SAVE_ERROR_COUNT) || 0;
  //The value is set in the Learnosity container component once Learnosity is ready
  const [saveState, setSaveState] = useState();
  const [saveErrorCount, setSaveErrorCount] = useState(__saveErrorCount);
  const [submitAttempts, setSubmitAttempts] = useState(0);
  const [submitInProgress, setSubmitInProgress] = useState();

  //Error dialogs
  const [openErrorDialog, setOpenErrorDialog] = useState(false);
  const [openContinueDialog, setOpenContinueDialog] = useState(false);
  const [openLaunchErrorDialog, setOpenLaunchErrorDialog] = useState(false);
  const [openSubmitDialog, setOpenSubmitDialog] = useState(false);
  const [openProctorPauseDialog, setOpenProctorPauseDialog] = useState(false);
  const [proctorPauseDialogProps, setProctorPauseDialogProps] =
    useState(PROCTOR_PAUSE_DIALOG());
  const [openExitingDialog, setOpenExitingDialog] = useState(false);
  const [exitingDialogDetails, setExitingDialogDetails] = useState();

  //Note: if assessment is loaded, it means user is at least in the "authenticated" status
  //The layout will also check for authToken and throw permission denied.
  const __storedTestStatus = getStorageItem(Storage.STORAGE_TEST_STATUS);

  const [testStatus, setTestStatus] = useState(__storedTestStatus);
  const [learnosityRequestObj, setLearnosityRequestObj] =
    useState(__signedObject);

  const __pnp = getStorageItem(Storage.STORAGE_PNP);
  const [pnp, setPnp] = useState(__pnp);

  const [learnosityItemApp, setLearnosityItemApp] = useState();
  const __storedPausedBy = getStorageItem(Storage.STORAGE_PAUSED_BY);
  const [pausedBy, setPausedBy] = useState(__storedPausedBy);
  const [exit, setExit] = useState(false);

  const updateLearnosityRequestObject = useCallback(
    (signedObject) => {
      setLearnosityRequestObj(signedObject);
      setStorageItem(Storage.STORAGE_SIGNED_OBJECT, signedObject);
    },
    [setStorageItem]
  );

  const updatePnp = useCallback(
    (pnp) => {
      setPnp(pnp);
      setStorageItem(Storage.STORAGE_PNP, pnp);
    },
    [setStorageItem]
  );

  //Save Error count
  const updateSaveErrorCount = useCallback(() => {
    setSaveErrorCount((prev) => {
      let newVal = prev + 1;
      setStorageItem(Storage.STORAGE_SAVE_ERROR_COUNT, newVal);
      return newVal;
    });
  }, [setStorageItem]);

  const updateSubmitAttempts = useCallback(() => {
    setSubmitAttempts((prev) => {
      let newVal = prev + 1;
      return newVal;
    });
  }, []);

  const resetSaveErrorCount = useCallback(() => {
    setSaveErrorCount(0);
    removeStorageItem(Storage.STORAGE_SAVE_ERROR_COUNT);
  }, [removeStorageItem]);

  const updateSaveState = useCallback(
    (state) => {
      setSaveState(state);
      setStorageItem(Storage.STORAGE_SAVE_STATE, state);
    },
    [setStorageItem]
  );

  const updateLaunchError = useCallback((state) => {
    setOpenLaunchErrorDialog(state);
  }, []);

  const applySaveStateChanges = useCallback(
    (state, incrErrorCount = false) => {
      updateSaveState(state);
      if (state === TestStatus.SAVE_ERROR && incrErrorCount) {
        updateSaveErrorCount();
      }
    },
    [updateSaveState, updateSaveErrorCount]
  );

  const updateTestStatus = useCallback(
    (status) => {
      setTestStatus(status);
      setStorageItem(Storage.STORAGE_TEST_STATUS, status);
    },
    [setStorageItem]
  );

  const updatePausedBy = useCallback(
    (status) => {
      setPausedBy(status);
      setStorageItem(Storage.STORAGE_PAUSED_BY, status);
    },
    [setStorageItem]
  );

  const resetPausedResumeRelatedStorage = useCallback(() => {
    setPausedBy(undefined);
    removeStorageItem(Storage.STORAGE_PAUSED_BY);
    removeStorageItem(Storage.STORAGE_PROCTOR_RESUME_NEEDED);
    removeStorageItem(Storage.STORAGE_PAUSED_REASON);
    removeStorageItem(Storage.STORAGE_PROHIBITED_ACTION);
    removeStorageItem(Storage.STORAGE_RESUMED_BY);
    removeStorageItem(Storage.STORAGE_STATUS_BEFORE_PAUSE);
  }, [removeStorageItem]);

  useEffect(() => {
    if (exit) {
      clearStorage();
      const isPreview = window.location?.pathname?.startsWith('/authorform');
      if (isPreview) {
        window.close();
      } else {
        if (platform === WINDOWS) {
          //There is no need to check just try to close the browser (Take a Test)
          closeWindowsSecureBrowser();
        } else if (isSecure && checkForSecureWrapper(platform, displayMode)) {
          //MacOS/iOS equivalent of the above - the wrapper hosts the PWA, so it
          //has to be told to close once we are done exiting.
          exitSecureWrapper();
        }
        i18n.changeLanguage(DEFAULT_LANGUAGE) //back on default language on exit
        let referrer = '/login';
        if (practice) {
          referrer =
            document.referrer || window.env.REACT_APP_PRACTICE_DEFAULT_REFERRER;
        }
        window.location.href = referrer;
      }
    }
  }, [exit, clearStorage, practice, platform, i18n, isSecure, displayMode]);

  return (
    <AssessmentContext.Provider
      value={{
        loginData,
        testStatus,
        learnosityRequestObj,
        openErrorDialog,
        openContinueDialog,
        openLaunchErrorDialog,
        openProctorPauseDialog,
        openSubmitDialog,
        proctorPauseDialogProps,
        saveState,
        saveErrorCount,
        learnosityItemApp,
        pausedBy,
        openExitingDialog,
        exitingDialogDetails,
        pnp,
        submitAttempts,
        submitInProgress,
        setSaveErrorCount,
        setLearnosityItemApp,
        updateLearnosityRequestObject,
        setOpenErrorDialog,
        setOpenContinueDialog,
        setOpenProctorPauseDialog,
        setOpenSubmitDialog,
        setProctorPauseDialogProps,
        updateSaveErrorCount,
        resetSaveErrorCount,
        updateSaveState,
        updateLaunchError,
        applySaveStateChanges,
        updateTestStatus,
        updatePausedBy,
        resetPausedResumeRelatedStorage,
        setExit,
        setOpenExitingDialog,
        setExitingDialogDetails,
        updatePnp,
        updateSubmitAttempts,
        setSubmitInProgress,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};
